import { createAction } from 'redux-actions';
import axios from 'axios';
import { get_service_endpoint } from '../../ServiceEndpoints.js';
import { setAuthError, logoutRequest } from '../actions/AuthActions';
import { AxiosCallError } from './commonActions.js';
import { updateAssessment } from './index.js';
import { stopAllStreams } from './AssesmentRecordingActions';
import { setSubmittedBy } from './AssessmentActions';
import { updateMeetingStatus } from './ProjectMeetingService';
import { history } from '../../index';

// Action types
export const SET_PROJECT_DATA = 'SET_PROJECT_DATA';
export const CLEAR_PROJECT_DATA = 'CLEAR_PROJECT_DATA';
export const SET_INVITE_DATA = 'SET_INVITE_DATA';
export const CLEAR_INVITE_DATA = 'CLEAR_INVITE_DATA';
export const SET_ASSESSMENT_DATA = 'SET_ASSESSMENT_DATA';
export const CLEAR_ASSESSMENT_DATA = 'CLEAR_ASSESSMENT_DATA';
export const SET_SESSION_DATA = 'SET_SESSION_DATA';
export const CLEAR_SESSION_DATA = 'CLEAR_SESSION_DATA';
export const SET_PROJECT_STEP = 'SET_PROJECT_STEP';
export const SET_MEDIA_PERMISSIONS = 'SET_MEDIA_PERMISSIONS';
export const SET_POD_CREATION_STATUS = 'SET_POD_CREATION_STATUS';
export const SET_IDE_URL = 'SET_IDE_URL';
export const UPDATE_ASSESSMENT_DATA = 'UPDATE_ASSESSMENT_DATA';
export const SET_ERROR = 'SET_ERROR';
export const CLEAR_ERROR = 'CLEAR_ERROR';
export const SET_LAUNCHING_SESSION = 'SET_LAUNCHING_SESSION';
export const SET_MODAL_SHOWN = 'SET_MODAL_SHOWN';

// Action creators
export const setProjectData = createAction(SET_PROJECT_DATA);
export const clearProjectData = createAction(CLEAR_PROJECT_DATA);
export const setInviteData = createAction(SET_INVITE_DATA);
export const clearInviteData = createAction(CLEAR_INVITE_DATA);
export const setAssessmentData = createAction(SET_ASSESSMENT_DATA);
export const clearAssessmentData = createAction(CLEAR_ASSESSMENT_DATA);
export const setSessionData = createAction(SET_SESSION_DATA);
export const clearSessionData = createAction(CLEAR_SESSION_DATA);
export const setProjectStep = createAction(SET_PROJECT_STEP);
export const setMediaPermissions = createAction(SET_MEDIA_PERMISSIONS);
export const setPodCreationStatus = createAction(SET_POD_CREATION_STATUS);
export const setIdeUrl = createAction(SET_IDE_URL);
export const updateAssessmentData = createAction(UPDATE_ASSESSMENT_DATA);
export const setError = createAction(SET_ERROR);
export const clearError = createAction(CLEAR_ERROR);
export const setLaunchingSession = createAction(SET_LAUNCHING_SESSION);
export const setModalShown = createAction(SET_MODAL_SHOWN);

// Utility function to convert snake_case to camelCase
const toCamelCase = obj => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toCamelCase);

  const camelObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      camelObj[camelKey] = toCamelCase(obj[key]);
    }
  }
  return camelObj;
};

// Thunk action to fetch invite data
export const fetchInviteData = inviteId => {
  const ep = get_service_endpoint('theia');
  return (dispatch, getState) => {
    return axios
      .get(`${ep}/api/v1/invites/${inviteId}`)
      .then(response => {
        if (response.data && response.data.data) {
          // Flatten the data - merge attributes with main data and convert to camelCase
          const inviteData = {
            ...response.data.data,
            ...toCamelCase(response.data.data.attributes),
          };
          localStorage.setItem('company_id', inviteData.companyId);
          dispatch(setInviteData(inviteData));
        }
      })
      .catch(error => {
        dispatch(
          setError({
            status: error?.status,
            message: error?.response?.data?.detail,
          }),
        );
      });
  };
};

export const fetchSesstionData = assessmentId => {
  const ep = get_service_endpoint('theia');
  return (dispatch, getState) => {
    return axios
      .get(`${ep}/api/v1/sessions?assessment_id=${assessmentId}`)
      .then(response => {
        if (response.data && response.data.data && response.data.data.length > 0) {
          const session = {
            ...response.data.data[0],
            ...toCamelCase(response.data.data[0].attributes),
          };
          dispatch(setSessionData(session));
        }
      })
      .catch(error => {
        dispatch(
          setError({
            status: error?.status,
            message: error?.response?.data?.detail,
          }),
        );
      });
  };
};

export const fetchAssessmentData = assessmentId => {
  const ep = get_service_endpoint('theia');
  return (dispatch, getState) => {
    return axios
      .get(`${ep}/api/v1/assessments/${assessmentId}`)
      .then(response => {
        if (response.data && response.data.data) {
          const assesment = {
            ...response.data.data,
            ...toCamelCase(response.data.data.attributes),
          };
          dispatch(setAssessmentData(assesment));
        }
      })
      .catch(error => {
        dispatch(
          setError({
            status: error?.status,
            message: error?.response?.data?.detail,
          }),
        );
      });
  };
};

export const acceptInvite = inviteToken => {
  return (dispatch, getState) => {
    const ep = get_service_endpoint('theia');
    axios
      .get(`${ep}/api/v1/invites/${inviteToken}/accept`)
      .then(response => {
        const currentInvite = getState().project.invite;
        if (currentInvite?.id) {
          fetchInviteData(currentInvite.id)(dispatch);
          dispatch(fetchProjectData(currentInvite.assessmentId));
        }
      })
      .catch(error => {
        dispatch(
          setError({
            status: error?.status,
            message: error?.response?.data?.detail,
          }),
        );
      });
  };
};

export const fetchProjectData = assessmentId => {
  return (dispatch, getState) => {
    return dispatch(fetchAssessmentData(assessmentId)).then(() => {
      const state = getState();
      if (state.project.assessment && state.project.assessment.status === 'in_progress') {
        return dispatch(fetchSesstionData(assessmentId));
      }
    });
  };
};

export const startAssessment = assessmentId => {
  const ep = get_service_endpoint('theia');
  return async dispatch => {
    try {
      dispatch(setLaunchingSession(true));
      const response = await axios.post(`${ep}/api/v1/sessions/launch`, {
        data: {
          type: 'session-launch',
          attributes: {
            assessment_id: assessmentId,
          },
        },
      });

      if (response.data && response.data.data) {
        // Flatten the data - merge attributes with main data and convert to camelCase
        const session = {
          ...response.data.data,
          ...toCamelCase(response.data.data.attributes),
        };
        dispatch(setSessionData(session));

        await fetchAssessmentData(assessmentId)(dispatch);
      }
    } catch (error) {
      dispatch(
        setError({
          status: error?.response?.status || 500,
          message: error?.response?.data?.detail || 'Failed to start assessment',
        }),
      );
    } finally {
      dispatch(setLaunchingSession(false));
    }
  };
};

export const submitProject = (assessmentId, meetingId) => {
  const ep = get_service_endpoint('theia');
  return dispatch => {
    return new Promise((resolve, reject) => {
      // Stop all media streams (video, audio, screen share)
      console.log('ids', assessmentId, meetingId);

      axios
        .patch(
          `${ep}/api/v1/assessments/${assessmentId}`,
          {
            status: 'submitted',
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .then(response => {
          if (response.data && response.data.data) {
            // Flatten the data - merge attributes with main data and convert to camelCase
            const assesment = {
              ...response.data.data,
              ...toCamelCase(response.data.data.attributes),
            };
            dispatch(updateAssessmentData(assesment));
          }

          // Get company id from local storage and save to session storage
          const companyId = localStorage.getItem('company_id');
          if (companyId) {
            sessionStorage.setItem('company_id', companyId);
          }

          if (meetingId) {
            dispatch(stopAllStreams());
            dispatch(updateMeetingStatus(meetingId, 'IN'));
          }

          // Set submitted by
          dispatch(setSubmittedBy('candidate'));

          // Logout after submitting the project
          dispatch(logoutRequest());

          // Redirect to survey
          history.push(`/survey/project/${assessmentId}`);

          // Reset modal state
          dispatch(setModalShown(false));

          resolve(response);
        })
        .catch(error => {
          dispatch(
            setError({
              status: error?.status,
              message: error?.response?.data?.detail,
            }),
          );
          reject(error);
        });
    });
  };
};

export const expireProject = assessmentId => {
  const ep = get_service_endpoint('theia');
  return dispatch => {
    axios
      .patch(
        `${ep}/api/v1/assessments/${assessmentId}`,
        {
          status: 'expired',
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .then(response => {
        if (response.data && response.data.data) {
          // Flatten the data - merge attributes with main data and convert to camelCase
          const assesment = {
            ...response.data.data,
            ...toCamelCase(response.data.data.attributes),
          };
          dispatch(updateAssessmentData(assesment));
        }
      })
      .catch(error => {
        dispatch(
          setError({
            status: error?.status,
            message: error?.response?.data?.detail,
          }),
        );
      });
  };
};
